import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { ChevronLeft, ChevronRight, Download, FilePen, Upload } from 'lucide-react';
import { EVENT_TYPE_LABEL, eventLabel } from '@wulftime/core';
import { api, download } from '../../lib/api';
import { fmtDateLong, fmtMinutes, fmtMonth, fmtTime, fmtWeekdayShort, monthISO } from '../../lib/format';
import { Alert, Button, Card, EmptyState, ErrorBox, Field, Input, Modal, PageHeader, Select, Spinner, Textarea, useToast } from '../../components/ui';
import { AnomalyBadge, DayTypeBadge } from '../../components/status';
import { RecordDetailView } from '../../components/record-detail';

export function MonthSwitcher({ month, onChange }: { month: string; onChange: (m: string) => void }) {
  const d = DateTime.fromISO(`${month}-01`);
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" onClick={() => onChange(d.minus({ months: 1 }).toFormat('yyyy-MM'))} aria-label="Mes anterior">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="min-w-[130px] text-center text-sm font-medium">{fmtMonth(month)}</span>
      <Button variant="ghost" size="sm" onClick={() => onChange(d.plus({ months: 1 }).toFormat('yyyy-MM'))} aria-label="Mes siguiente">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

const LATE_LABEL: Record<string, string> = { FORGOT: 'Olvido de fichar la salida', PERSONAL: 'Tiempo personal (no trabajado)', WORKED: 'Tiempo trabajado de más' };

/** Revisiones de la empresa sobre las salidas tardías del mes y saldo de la bolsa de horas. */
function LateExitsCard({ month }: { month: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ['me', 'late-exits', month], queryFn: () => api.get<any>(`/api/v1/me/late-exits?period=${month}`) });
  const [dispute, setDispute] = useState<{ id: string; reason: string } | null>(null);
  const send = useMutation({
    mutationFn: () => api.post(`/api/v1/me/late-exits/${dispute!.id}/dispute`, { reason: dispute!.reason }),
    onSuccess: () => (toast.success('Enviado a tu responsable.'), setDispute(null), qc.invalidateQueries({ queryKey: ['me'] })),
    onError: toast.error,
  });
  const reviewed: any[] = data?.reviewed ?? [];
  const bank = data?.bank;
  if (!reviewed.length && !bank?.enabled) return null;
  return (
    <Card title="Revisión de salidas y bolsa de horas" className="mb-4">
      {bank?.enabled && (
        <p className="mb-2 text-sm">
          Saldo de tu bolsa de horas este mes: <strong>{fmtMinutes(bank.balanceMinutes, { sign: true })}</strong>
          <span className="text-xs text-slate-500"> (trabajado de más {fmtMinutes(bank.workedExtraMinutes)} · horas que faltaron {fmtMinutes(bank.deficitMinutes)})</span>
        </p>
      )}
      <ul className="divide-y divide-slate-100 text-sm">
        {reviewed.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span>
              <strong className="capitalize">{fmtWeekdayShort(r.workDate)}</strong>: saliste a las {fmtTime(r.actualOut)} (+{r.excessMinutes} min) · {LATE_LABEL[r.classification]}
              {r.realEnd ? `, fin real ${fmtTime(r.realEnd)}` : ''}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setDispute({ id: r.id, reason: '' })}>
              No estoy de acuerdo
            </Button>
          </li>
        ))}
      </ul>
      <Modal
        open={Boolean(dispute)}
        onClose={() => setDispute(null)}
        title="No estoy de acuerdo"
        footer={
          <Button loading={send.isPending} disabled={(dispute?.reason.trim().length ?? 0) < 5} onClick={() => send.mutate()}>
            Enviar a mi responsable
          </Button>
        }
      >
        <Field label="Explica qué pasó" required>
          {(id) => <Textarea id={id} value={dispute?.reason ?? ''} onChange={(e) => setDispute({ ...dispute!, reason: e.target.value })} placeholder="Ej.: me quedé terminando un pedido" />}
        </Field>
      </Modal>
    </Card>
  );
}

export function WorkerHistory() {
  const [month, setMonth] = useState(monthISO());
  const navigate = useNavigate();
  const from = `${month}-01`;
  const to = DateTime.fromISO(from).endOf('month').toISODate()!;
  const { data, isLoading } = useQuery({ queryKey: ['me', 'records', month], queryFn: () => api.get<any[]>(`/api/v1/me/records?from=${from}&to=${to}`) });
  const { data: summary } = useQuery({ queryKey: ['me', 'summary', month], queryFn: () => api.get(`/api/v1/me/summary?month=${month}`) });
  const toast = useToast();
  return (
    <div>
      <PageHeader
        title="Historial"
        description="Tus jornadas: hora real fichada, tiempo computable y diferencias con lo previsto."
        actions={
          <Button variant="secondary" size="sm" icon={<Download className="h-4 w-4" />} onClick={() => download(`/api/v1/me/reports/${month}`).catch(toast.error)}>
            Registro mensual (PDF)
          </Button>
        }
      />
      <div className="mb-4 flex items-center justify-between">
        <MonthSwitcher month={month} onChange={setMonth} />
      </div>
      {summary && (
        <div className="mb-4 grid grid-cols-3 gap-3 text-center">
          <div className="card p-3">
            <p className="text-xs text-slate-500">Previstas</p>
            <p className="font-semibold tabular-nums">{fmtMinutes(summary.plannedMinutes)}</p>
          </div>
          <div className="card p-3">
            <p className="text-xs text-slate-500">Computables</p>
            <p className="font-semibold tabular-nums">{fmtMinutes(summary.effectiveMinutes)}</p>
          </div>
          <div className="card p-3">
            <p className="text-xs text-slate-500">Días trabajados</p>
            <p className="font-semibold tabular-nums">{summary.workedDays}</p>
          </div>
        </div>
      )}
      <LateExitsCard month={month} />
      {isLoading ? (
        <Spinner />
      ) : !data?.length ? (
        <Card>
          <EmptyState title="Sin jornadas este mes" />
        </Card>
      ) : (
        <ul className="space-y-2">
          {data.map((r) => (
            <li key={r.id}>
              <button onClick={() => navigate(`/app/history/${r.id}`)} className="card flex w-full items-center justify-between gap-3 p-4 text-left transition hover:border-brand-300">
                <div className="min-w-0">
                  <p className="font-medium capitalize">{fmtWeekdayShort(r.workDate)}</p>
                  <p className="text-xs text-slate-500">
                    {r.computed?.real.firstIn ? `${fmtTime(r.computed.real.firstIn, r.planned.timezone)} – ${r.computed.real.lastOut ? fmtTime(r.computed.real.lastOut, r.planned.timezone) : '…'}` : r.dayType === 'WORKDAY' ? 'Sin fichajes' : ''}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.dayType !== 'WORKDAY' && <DayTypeBadge dayType={r.dayType} label={r.planned.label} />}
                    {r.anomalyCodes.slice(0, 2).map((a: string) => (
                      <AnomalyBadge key={a} code={a} />
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold tabular-nums">{fmtMinutes(r.effectiveMinutes)}</p>
                  <p className="text-xs text-slate-500 tabular-nums">de {fmtMinutes(r.plannedMinutes)}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CorrectionModal({ open, onClose, workDate, events, timezone, target, intro }: { open: boolean; onClose: () => void; workDate: string; events: any[]; timezone: string; target?: any; intro?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<'ADD_EVENT' | 'CHANGE_TIME' | 'VOID_EVENT'>(target ? 'CHANGE_TIME' : 'ADD_EVENT');
  const [eventId, setEventId] = useState<string>(target?.id ?? '');
  const [eventType, setEventType] = useState('CLOCK_OUT');
  const [time, setTime] = useState(target ? fmtTime(target.occurredAt, timezone) : '');
  const [nextDay, setNextDay] = useState(false);
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const m = useMutation({
    mutationFn: async () => {
      let evidenceDocumentId: string | undefined;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        evidenceDocumentId = (await api.post<{ id: string }>('/api/v1/uploads', fd)).id;
      }
      const proposedAt = kind === 'VOID_EVENT' ? undefined : DateTime.fromISO(`${workDate}T${time}`, { zone: timezone }).plus({ days: nextDay ? 1 : 0 }).toUTC().toISO();
      return api.post('/api/v1/corrections', { workDate, kind, targetEventId: kind === 'ADD_EVENT' ? undefined : eventId, eventType: kind === 'ADD_EVENT' ? eventType : undefined, proposedAt, reason, evidenceDocumentId });
    },
    onSuccess: (r: any) => {
      if (r?.autoApproved) toast.success('Corrección aplicada: tu empresa aprueba automáticamente los olvidos de fichaje.');
      else toast.success(r?.autoApproval?.reason ? `Solicitud enviada a tu responsable (${r.autoApproval.reason.replace(/\.$/, '')}).` : 'Solicitud enviada a tu responsable.');
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['corrections'] });
      onClose();
    },
  });
  const active = events.filter((e) => e.status === 'ACTIVE' && e.type !== 'AUTO_START');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitar corrección"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={m.isPending} disabled={reason.trim().length < 5 || (kind !== 'VOID_EVENT' && !time) || (kind !== 'ADD_EVENT' && !eventId)} onClick={() => m.mutate()}>
            Enviar solicitud
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {intro && <Alert tone="warning">{intro}</Alert>}
        <Alert tone="info">Tu fichaje original no se modifica: tu responsable revisará la solicitud y, si la aprueba, se registrará una corrección con su motivo.</Alert>
        <ErrorBox error={m.error} />
        <Field label="¿Qué necesitas corregir?">
          {(id) => (
            <Select id={id} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="ADD_EVENT">Añadir un fichaje olvidado</option>
              <option value="CHANGE_TIME" disabled={!active.length}>
                Cambiar la hora de un fichaje
              </option>
              <option value="VOID_EVENT" disabled={!active.length}>
                Anular un fichaje erróneo
              </option>
            </Select>
          )}
        </Field>
        {kind === 'ADD_EVENT' ? (
          <Field label="Tipo de fichaje">
            {(id) => (
              <Select id={id} value={eventType} onChange={(e) => setEventType(e.target.value)}>
                {(['CLOCK_IN', 'BREAK_START', 'BREAK_END', 'CLOCK_OUT'] as const).map((t) => (
                  <option key={t} value={t}>
                    {EVENT_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <Field label="Fichaje">
            {(id) => (
              <Select id={id} value={eventId} onChange={(e) => setEventId(e.target.value)}>
                <option value="">Selecciona…</option>
                {active.map((e) => (
                  <option key={e.id} value={e.id}>
                    {eventLabel(e)} · {fmtTime(e.occurredAt, timezone)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {kind !== 'VOID_EVENT' && (
          <div className="flex items-end gap-3">
            <Field label="Hora correcta" className="flex-1">
              {(id) => <Input id={id} type="time" value={time} onChange={(e) => setTime(e.target.value)} />}
            </Field>
            <label className="mb-2 flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={nextDay} onChange={(e) => setNextDay(e.target.checked)} /> Día siguiente
            </label>
          </div>
        )}
        <Field label="Motivo" required>{(id) => <Textarea id={id} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explica qué ocurrió" />}</Field>
        <Field label="Justificante (opcional)" hint="PDF o imagen, máx. 10 MB.">
          {(id) => (
            <label htmlFor={id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
              <Upload className="h-4 w-4" /> {file ? file.name : 'Seleccionar archivo'}
              <input id={id} type="file" accept="application/pdf,image/*" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          )}
        </Field>
      </div>
    </Modal>
  );
}

export function WorkerRecordDetail() {
  const { id } = useParams();
  const [modal, setModal] = useState<{ target?: any; intro?: string } | null>(null);
  const [params, setParams] = useSearchParams();
  const { data, isLoading, error } = useQuery({ queryKey: ['me', 'record', id], queryFn: () => api.get(`/api/v1/me/records/${id}`) });
  // Desde el aviso de salida/jornada automática («?corregir=…»): abre la corrección ya preparada
  useEffect(() => {
    const want = params.get('corregir');
    if (!want || !data) return;
    const autoOut = data.events.find((e: any) => e.type === 'CLOCK_OUT' && e.source === 'SYSTEM' && e.status === 'ACTIVE');
    setModal(
      want === 'salida' && autoOut
        ? { target: autoOut, intro: `Se registró tu salida automáticamente a las ${fmtTime(autoOut.occurredAt, data.record.timezone)}. Indica la hora a la que saliste de verdad.` }
        : { intro: 'Esta jornada se registró automáticamente con el horario previsto. Indica qué fichajes faltan o cuáles no son correctos.' },
    );
    params.delete('corregir');
    setParams(params, { replace: true });
  }, [params, data, setParams]);
  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const r = data.record;
  return (
    <div>
      <PageHeader
        back={
          <Link to="/app/history" className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <ChevronLeft className="h-4 w-4" /> Historial
          </Link>
        }
        title={fmtDateLong(r.workDate)}
        actions={
          <Button icon={<FilePen className="h-4 w-4" />} onClick={() => setModal({})}>
            Solicitar corrección
          </Button>
        }
      />
      <RecordDetailView
        data={data}
        actions={(e) =>
          e.status === 'ACTIVE' && e.type !== 'AUTO_START' ? (
            <Button size="sm" variant="ghost" onClick={() => setModal({ target: e })}>
              Corregir
            </Button>
          ) : null
        }
      />
      {modal && <CorrectionModal open onClose={() => setModal(null)} workDate={r.workDate} events={data.events} timezone={r.timezone} target={modal.target} intro={modal.intro} />}
    </div>
  );
}
